import React from 'react';
import PropTypes from 'prop-types';
import { formatBigNumber } from '../../../SleepHub/sleepHubApi';
import BarList from './GameBars';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  PANEL_ROW_CLASS,
  STAT_ICONS
} from '../../../../component/common/designSystem';
import { QUIZ_STATUS_RAMP, ACCURACY_RAMP, SEQUENCE_RAMP, RANK_RAMP } from './gamesTheme';
import {
  useGameDetail,
  orDash,
  TrendPanel,
  WeekdayPanel,
  GameFootnote,
  BandBar
} from './GameShell';

/**
 * Games → You Quiz.
 *
 * A play here is a QUIZ ATTEMPT, and this is the only game of the four with
 * nine distinct modes behind one name — Quiz Zone, Guess the Word, Math Quiz,
 * Contest, Exam and the rest all write to the same collection. Reporting "the
 * quiz" as one thing would hide the only interesting question: which modes hold
 * people and which they walk out of.
 *
 * THE ABANDONMENT COLUMN IS THE POINT OF THE MODE TABLE. A mode nobody finishes
 * is too hard, too long, or broken — and it is invisible in an attempt count,
 * because starting a quiz and abandoning it looks exactly like playing one.
 *
 * "STILL OPEN" AND "WALKED AWAY" ARE KEPT APART, because they are different
 * facts. An attempt sitting `active` is somebody who might yet come back; an
 * `abandoned` one is somebody who said they would not. Collapsing them into a
 * single "didn't finish" would make an in-progress quiz look like a rejection.
 *
 * ACCURACY IS A GROUP FIGURE. It appears nowhere near a name, and there is no
 * ranking of who scored what — the app has its own leaderboard for people who
 * want that.
 */
const QuizPage = ({ fetcher, populationLabel = 'users' }) => {
  const [stats, gate] = useGameDetail(fetcher);
  if (gate) return gate;

  return (
    <div>
      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People Playing"
          value={formatBigNumber(stats?.players || 0)}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Quizzes Started"
          value={formatBigNumber(stats?.plays || 0)}
        />
        <StatCard icon={STAT_ICONS.goals} title="Finished" value={`${stats?.finishRate ?? 0}%`} />
        <StatCard
          icon={STAT_ICONS.score}
          title="Answered Correctly"
          value={`${stats?.accuracy ?? 0}%`}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Questions Answered"
          value={formatBigNumber(stats?.questionsAnswered || 0)}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Days Played On"
          value={formatBigNumber(stats?.activeDays || 0)}
        />
      </div>

      {/* ---------- What happens to an attempt ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="How Attempts End"
          subtitle="Finished, still open, or walked away from"
          data={stats?.statusSplit || []}
          colors={QUIZ_STATUS_RAMP}
          unit="attempts"
          totalLabel="Attempts"
          legendLimit={3}
        />
        <Panel title="How Accurate" subtitle="Correct answers as a share of one attempt">
          {(stats?.accuracySplit || []).some((row) => row.count > 0) ? (
            <BandBar rows={stats.accuracySplit} colors={ACCURACY_RAMP} unit="attempts" />
          ) : (
            <PanelEmpty>No questions answered yet.</PanelEmpty>
          )}
        </Panel>
        <Panel title="How Long an Attempt Is" subtitle="Questions attempted in one sitting">
          {(stats?.questionSplit || []).some((row) => row.count > 0) ? (
            <BandBar rows={stats.questionSplit} colors={SEQUENCE_RAMP} unit="attempts" />
          ) : (
            <PanelEmpty>No attempts yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Which modes ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <Panel title="Which Quiz Modes" subtitle="Attempts by mode, most played first">
          {(stats?.typeSplit || []).length > 0 ? (
            <BarList
              rows={stats.typeSplit}
              unit="attempts"
              colorFor={(index) => RANK_RAMP[index % RANK_RAMP.length]}
            />
          ) : (
            <PanelEmpty>No attempts yet.</PanelEmpty>
          )}
        </Panel>
        <MiniDonutCard
          title="How Much People Play"
          subtitle="Attempts per person"
          data={stats?.volumeSplit || []}
          colors={SEQUENCE_RAMP}
          unit="people"
          totalLabel="Players"
          legendLimit={5}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
          {/* Kept apart from "finished" on purpose: an open attempt is somebody
              who may return, an abandoned one is somebody who chose not to. */}
          <StatCard
            icon={STAT_ICONS.more}
            title="Walked Away"
            value={`${stats?.abandonRate ?? 0}%`}
          />
          <StatCard
            icon={STAT_ICONS.time}
            title="Per Question"
            value={stats?.avgSecondsPerQuestion == null ? '-' : `${stats.avgSecondsPerQuestion}s`}
          />
          <StatCard
            icon={STAT_ICONS.activeUsers}
            title={`Share of ${populationLabel}`}
            value={
              stats?.totalUsers > 0
                ? `${Math.round(((stats.players || 0) / stats.totalUsers) * 1000) / 10}%`
                : '-'
            }
          />
        </div>
      </div>

      {/* ---------- Mode by mode ---------- */}
      <div className="mt-6">
        <Panel
          title="Mode by Mode"
          subtitle="A mode nobody finishes is too hard, too long, or broken — a dash means too few attempts to report"
        >
          {(stats?.typeTable || []).length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Mode</th>
                  <th className="py-2 px-4 font-medium text-center">Attempts</th>
                  <th className="py-2 px-4 font-medium text-center">People</th>
                  <th className="py-2 px-4 font-medium text-center">Correct</th>
                  <th className="py-2 px-4 font-medium text-center">Finished</th>
                  <th className="py-2 font-medium text-right">Walked away</th>
                </>
              }
            >
              {stats.typeTable.map((row) => (
                <tr key={row.label} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{row.label}</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.attempts)}</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.players)}</td>
                  <td className="py-2 px-4 text-center">
                    {row.accuracy == null ? '-' : `${row.accuracy}%`}
                  </td>
                  <td className="py-2 px-4 text-center">
                    {row.finishRate == null ? '-' : `${row.finishRate}%`}
                  </td>
                  <td className="py-2 text-right">
                    {row.abandonRate == null ? '-' : `${row.abandonRate}%`}
                  </td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No quiz mode has enough attempts to report.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Rhythm ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <TrendPanel stats={stats} subtitle="Attempts per day — last 30 days" />
        <WeekdayPanel stats={stats} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-6">
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Attempts Each"
          value={stats?.playsPerPlayer ?? 0}
        />
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Questions per Attempt"
          value={orDash(stats?.avgQuestionsPerAttempt)}
        />
        <StatCard
          icon={STAT_ICONS.personalBest}
          title="Average Score"
          value={orDash(stats?.avgScore)}
        />
        <StatCard icon={STAT_ICONS.plays} title="Still Open" value={`${stats?.openRate ?? 0}%`} />
      </div>

      <GameFootnote />
    </div>
  );
};

QuizPage.propTypes = {
  fetcher: PropTypes.func.isRequired,
  populationLabel: PropTypes.string
};

export default QuizPage;
